import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Popover from '../../misc/Popover';
import InputSettingsField from './InputSettingsField';
import ColorPickerSettings from './ColorPickerSettings';
import { StyledFieldWrapper, StyledFieldLabel } from './SettingsCommonElements';

import DecoratorIconOptions from '../../../constants/DecoratorIconOptions';

// ===================================================================
// Styled Components
// ===================================================================

const StyledSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSelect = styled.select`
  padding: 4px 6px;
  font-size: 12px;
  border-radius: ${(props) => props.theme?.borderRadius || 5}px;
  border: 1px solid ${(props) => props.theme?.borderColor || '#d9d9d9'};
  background-color: #fff;
  cursor: pointer;
  width: 100%;
`;

const StyledValidationMessage = styled.div`
  color: #f5222d;
  font-size: 11px;
  margin-top: 2px;
`;

// ===================================================================
// Component
// ===================================================================

const DecoratorSettingsModal = ({
  display,
  decorator,
  existingActions,
  onHide,
  onSubmit,
  children,
}) => {

  const [form, setForm] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const nextForm = decorator
      ? { ...decorator }
      : { icon: '', label: '', action: '', color: '' };

    setForm(nextForm);
    setSubmitted(false);
  }, [decorator]);

  // ===================================================================
  // Handlers
  // ===================================================================

  const handleInputChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isDuplicateAction = form.action && (existingActions || []).includes(form.action);

  const handleSubmit = () => {
    setSubmitted(true);

    if (!form.action) return;
    if (isDuplicateAction) return;
    if (!form.icon && !form.label) return;
    if (!form.color) return;

    const nextForm = { ...form };

    if (onSubmit) onSubmit(nextForm._id, nextForm);

    handleHide();
  };

  const handleHide = () => {
    setForm({});
    setSubmitted(false);
    if (onHide) onHide();
  };

  // ===================================================================
  // Render
  // ===================================================================

  const iconOptions = DecoratorIconOptions;
  const hasValidationError = submitted && (!form.action || isDuplicateAction || (!form.icon && !form.label) || !form.color);

  return (
    <Popover
      reposition={true}
      positions={['left', 'top', 'bottom']}
      isOpen={display}
      onClose={handleHide}
      onSubmit={handleSubmit}
      content={(
        <>
          <StyledFieldWrapper>
            <StyledSelectWrapper>
              <StyledFieldLabel>Icon</StyledFieldLabel>
              <StyledSelect
                value={form.icon || ''}
                onChange={(e) => handleInputChange('icon', e.target.value)}
              >
                <option value="">None</option>
                {iconOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </StyledSelect>
            </StyledSelectWrapper>
          </StyledFieldWrapper>

          <StyledFieldWrapper>
            <InputSettingsField
              inputStyles={{ padding: 4 }}
              label="Label"
              value={form.label || ''}
              type="text"
              onChange={(fieldId, value) => handleInputChange('label', value)}
            />
            {submitted && !form.icon && !form.label ? (
              <StyledValidationMessage>Icon or label is required</StyledValidationMessage>
            ) : null}
          </StyledFieldWrapper>

          <StyledFieldWrapper>
            <InputSettingsField
              inputStyles={{ padding: 4 }}
              label="Action"
              value={form.action || ''}
              type="text"
              onChange={(fieldId, value) => handleInputChange('action', value)}
            />
            {submitted && !form.action ? (
              <StyledValidationMessage>Action is required</StyledValidationMessage>
            ) : submitted && isDuplicateAction ? (
              <StyledValidationMessage>Action must be unique</StyledValidationMessage>
            ) : null}
          </StyledFieldWrapper>

          <StyledFieldWrapper>
            <StyledFieldLabel>Color</StyledFieldLabel>
            <ColorPickerSettings
              value={form.color || ''}
              onChange={(value) => handleInputChange('color', value)}
            />
          </StyledFieldWrapper>
        </>
      )}
    >
      {children}
    </Popover>
  );

};

DecoratorSettingsModal.propTypes = {
  display: PropTypes.bool,
  decorator: PropTypes.object,
  existingActions: PropTypes.array,
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
  children: PropTypes.node,
};

export default DecoratorSettingsModal;
